"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { CalendarCheck, Check, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { STUDY_PLAN_LABELS } from "@/lib/study-plan";
import type {
  StudyPageScheduleResult,
  StudyScheduleChoice,
} from "@/lib/study-page-schedule";
import { cn } from "@/lib/utils";

const dateLabel = (dateISO: string) =>
  new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
    weekday: "short",
    timeZone: "Asia/Seoul",
  }).format(new Date(`${dateISO}T00:00:00+09:00`));
const languageLabel = (lang: string) =>
  ({ c: "C", java: "Java", python: "Python" })[lang];
const choiceKey = (choice: StudyScheduleChoice) =>
  `${choice.examSessionId}:${choice.planId}:${choice.taskKey}`;

/** 실제 피드백 UI. 선택 결과만 전달하며 출석 횟수는 변경하지 않는다. */
export default function StudyScheduleFeedback({
  initialResult,
  onComplete,
}: {
  initialResult: StudyPageScheduleResult;
  onComplete: (
    choice: StudyScheduleChoice,
    automatic: boolean,
  ) => Promise<{ success: boolean; error?: string }>;
}) {
  const [result, setResult] = useState(initialResult);
  const [open, setOpen] = useState(initialResult.kind === "future");
  const [selectedKey, setSelectedKey] = useState(
    initialResult.kind === "future" ? choiceKey(initialResult.choices[0]) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(false);
  if (result.kind === "none") return null;

  async function complete(choice: StudyScheduleChoice, automatic: boolean) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const saved = await onComplete(choice, automatic);
      if (!saved.success) {
        setError(saved.error ?? "일정 저장을 다시 시도해주세요.");
        return;
      }
      setResult({ kind: "completed", task: choice });
      setOpen(false);
    } catch {
      setError("출석은 저장되어 있어요. 일정 체크만 다시 시도해주세요.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  const choices = result.kind === "future" ? result.choices : [];
  const selected = choices.find((choice) => choiceKey(choice) === selectedKey);
  return (
    <>
      <aside
        aria-label="학습 일정 연결 결과"
        className="not-prose my-4 rounded-xl border border-border bg-muted/40 p-4 text-sm text-foreground"
      >
        <div className="flex items-start gap-3">
          <CalendarCheck
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="font-semibold" role="status">
              {result.kind === "completed"
                ? "공부한 내용을 일정에도 체크했어요"
                : result.kind === "future"
                  ? "미리 공부한 일정이 있나요?"
                  : "출석은 저장했어요"}
            </p>
            <p className="mt-1 break-words text-muted-foreground">
              {result.kind === "completed"
                ? `${dateLabel(result.task.dateISO)} · ${result.task.title} · ${result.task.reviewRound}회독`
                : result.kind === "future"
                  ? "예정된 학습 항목을 확인하고 완료로 표시할 수 있어요."
                  : result.error}
            </p>
            {error && (
              <p className="mt-2 text-red-700 dark:text-red-300" role="alert">
                {error}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-4">
              {result.kind === "future" && (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="min-h-11 rounded font-semibold underline underline-offset-4 hover:text-amber-700 dark:hover:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  예정된 일정 보기
                </button>
              )}
              {result.kind === "error" && result.retryTask && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void complete(result.retryTask!, true)}
                  className="min-h-11 rounded font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-50"
                >
                  {busy ? "저장 중…" : "일정 연결 다시 시도"}
                </button>
              )}
              <Link
                href="/my/schedule"
                className="inline-flex min-h-11 items-center gap-1 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                {result.kind === "completed"
                  ? "일정에서 변경"
                  : "학습 일정 보기"}
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </aside>
      {result.kind === "future" && (
        <Dialog
          open={open}
          onOpenChange={(next) => {
            if (!busy) setOpen(next);
          }}
        >
          <DialogContent className="max-h-[85dvh] overflow-y-auto overscroll-contain sm:max-w-lg">
            <DialogHeader>
              <div className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <Check className="h-3.5 w-3.5" aria-hidden />
                출석 저장 완료
              </div>
              <DialogTitle className="pr-5 text-xl leading-snug">
                미리 공부한 일정으로 체크할까요?
              </DialogTitle>
              <DialogDescription className="pt-1 leading-relaxed">
                같은 내용을 공부할 예정인 일정이에요.
                <br />
                오늘 공부한 항목을 하나 선택해주세요.
              </DialogDescription>
            </DialogHeader>
            <fieldset disabled={busy} className="space-y-2">
              <legend className="sr-only">완료로 표시할 예정 일정</legend>
              {choices.map((choice) => {
                const key = choiceKey(choice);
                const checked = key === selectedKey;
                const language =
                  choice.displayLanguage || languageLabel(choice.codeLang);
                return (
                  <label
                    key={key}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors focus-within:ring-2 focus-within:ring-amber-500",
                      checked
                        ? "border-amber-500 bg-amber-50 dark:bg-amber-950/40"
                        : "border-border bg-background hover:bg-muted/60",
                      busy && "cursor-wait opacity-60",
                    )}
                  >
                    <input
                      type="radio"
                      name="future-study-task"
                      value={key}
                      checked={checked}
                      onChange={() => setSelectedKey(key)}
                      className="mt-1 h-4 w-4 shrink-0 accent-amber-600"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-amber-800 dark:text-amber-200">
                        {dateLabel(choice.dateISO)}
                      </span>
                      <span className="mt-1 block break-words text-base font-bold text-foreground">
                        {choice.title}
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{STUDY_PLAN_LABELS[choice.planId]}</span>
                        <span aria-hidden>·</span>
                        {language && (
                          <>
                            <span>{language}</span>
                            <span aria-hidden>·</span>
                          </>
                        )}
                        <span className="rounded bg-background/80 px-1.5 py-0.5 font-medium text-foreground">
                          {choice.reviewRound}회독
                        </span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
            {error && (
              <p
                role="alert"
                className="text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </p>
            )}
            <p className="text-xs leading-relaxed text-muted-foreground">
              선택한 일정만 완료돼요. 다른 일정과 출석·회독 기록은 그대로
              유지돼요.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row-reverse">
              <button
                type="button"
                disabled={busy || !selected}
                onClick={() => {
                  if (selected) void complete(selected, false);
                }}
                className="min-h-11 flex-1 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
              >
                {busy ? "저장 중…" : "선택한 일정 완료로 표시"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                className="min-h-11 rounded-lg border border-border px-4 py-2.5 text-sm text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-50"
              >
                일정은 그대로 둘게요
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
